/**
 * usePageviewTracker
 *
 * Fires a `POST /api/analytics/pageview` beacon on the first mount and on
 * every route change. Uses `sendBeacon` when available (survives page unload).
 * The session id is persisted in localStorage so the same visitor returning
 * later still counts as the same "unique".
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'am_session_id';

const getOrCreateSessionId = () => {
  try {
    let sid = window.localStorage.getItem(STORAGE_KEY);
    if (!sid) {
      const rand = window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      sid = rand.replace(/-/g, '').slice(0, 32);
      window.localStorage.setItem(STORAGE_KEY, sid);
    }
    return sid;
  } catch {
    // localStorage blocked (e.g. private mode) — fall back to a per-tab id.
    return Math.random().toString(36).slice(2, 12);
  }
};

const sendBeacon = (payload) => {
  const url = `${API}/analytics/pageview`;
  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Analytics must never break the app — swallow every error.
  }
};

export const usePageviewTracker = () => {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    // Skip tracking the admin panel — owners don't want to inflate their own numbers.
    if (location.pathname.startsWith('/admin')) return;
    if (location.pathname === lastPath.current) return;
    lastPath.current = location.pathname;

    sendBeacon({
      path: location.pathname + location.search,
      session_id: getOrCreateSessionId(),
      referrer: document.referrer || null,
    });
  }, [location.pathname, location.search]);
};
